import React from "react";
import { parseBooleanVal } from "Utils/utils";

const EMPTY_PLACEHOLDER = "—";

const formatFieldLabel = (label) => {
  if (!label) return "";
  return String(label)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const FormFieldHint = ({ hint }) => {
  if (!hint) return null;
  return (
    <div
      className="form-field-hint"
      dangerouslySetInnerHTML={{ __html: hint }}
    />
  );
};

export const FormReadOnlyField = ({
  label,
  value,
  fullWidth = false,
  hint,
  multiline = false,
  className = "",
}) => {
  const displayValue =
    value === null || value === undefined || value === ""
      ? EMPTY_PLACEHOLDER
      : value;
  const isEmpty = displayValue === EMPTY_PLACEHOLDER;

  return (
    <div
      className={`form-readonly-field ${
        fullWidth ? "col-span-12" : "col-span-12 md:col-span-6"
      } ${className}`}
    >
      <span className="form-readonly-label">{formatFieldLabel(label)}</span>
      <div
        className={`form-readonly-value ${isEmpty ? "is-empty" : ""} ${
          multiline ? "is-multiline" : ""
        }`}
      >
        {displayValue}
      </div>
      <FormFieldHint hint={hint} />
    </div>
  );
};

export const FormReadOnlyChoice = ({
  label,
  options = [],
  selectedValue,
  fullWidth = false,
  hint,
  type = "radio",
}) => {
  const formattedLabel = formatFieldLabel(label);

  if (type === "checkbox") {
    const checked = parseBooleanVal(selectedValue);

    return (
      <div
        className={`form-readonly-field ${
          fullWidth ? "col-span-12" : "col-span-12 md:col-span-6"
        }`}
      >
        <div className="form-checkbox-card">
          <input type="checkbox" checked={checked} disabled readOnly />
          <label>{formattedLabel}</label>
        </div>
        <FormFieldHint hint={hint} />
      </div>
    );
  }

  return (
    <div
      className={`form-readonly-field ${
        fullWidth ? "col-span-12" : "col-span-12 md:col-span-6"
      }`}
    >
      <span className="form-readonly-label">{formattedLabel}</span>
      <div className="form-choice-group">
        {options.map((option, idx) => {
          const optionLabel = option.lable ?? option.label ?? option;
          const isSelected = String(optionLabel) === String(selectedValue);
          return (
            <span
              key={idx}
              className={`form-choice-badge ${
                isSelected ? "is-selected" : "is-unselected"
              }`}
            >
              {optionLabel}
            </span>
          );
        })}
      </div>
      <FormFieldHint hint={hint} />
    </div>
  );
};

export const FormReadOnlySignature = ({ label, imageSrc, dateValue }) => (
  <div
    className={`form-signature-display ${
      label ? "col-span-12 md:col-span-6" : "col-span-12"
    }`}
  >
    {label && (
      <span className="form-readonly-label">{formatFieldLabel(label)}</span>
    )}
    <div className="form-signature-display-inner">
      {imageSrc ? (
        <img src={imageSrc} alt="Signature" className="form-signature-image" />
      ) : (
        <div className="form-signature-empty">Not signed</div>
      )}
    </div>
    {dateValue !== undefined && (
      <div className="form-signature-date">
        <span className="form-readonly-label">Signature date</span>
        <span className="form-signature-date-value">
          {dateValue || EMPTY_PLACEHOLDER}
        </span>
      </div>
    )}
  </div>
);

export const FormReadOnlyTable = ({ label, columns = [], rows = [], hint }) => {
  const hasRows = rows.length > 0;

  return (
    <div className="col-span-12 form-readonly-field">
      <span className="form-readonly-label">{formatFieldLabel(label)}</span>
      <div className="form-table-wrapper custom-scroll max-h-[420px]">
        <table className="form-table form-readonly-table">
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th key={idx}>{col.lable ?? col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!hasRows ? (
              <tr>
                <td colSpan={columns.length || 1} className="form-table-empty">
                  No entries recorded
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((col, ci) => (
                    <td key={ci}>
                      {col.type === "checkbox"
                        ? parseBooleanVal(col.value)
                          ? "Yes"
                          : "No"
                        : col.value ?? "—"}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <FormFieldHint hint={hint} />
    </div>
  );
};

export default FormReadOnlyField;
